import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import { LightboxModal } from "../components/LightboxModal";
import { WebForm } from "../components/WebForm";
import { COMPANY_INFO, SERVICES_LIST } from "../data/company";
import { fetchCmsCollection } from "../lib/cms";

export function HomePage() {
  const [galleries, setGalleries] = useState([]);
  const [selectedGalleryImages, setSelectedGalleryImages] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Slider state
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const fallbackImages = [
    "https://assets.auxodev.dk/assets/2f17f913-822d-430f-9ea1-0cc9e6665c42/f8a25502-6827-4d1f-abb8-233ce87e9511.jpg",
    "https://assets.auxodev.dk/assets/2f17f913-822d-430f-9ea1-0cc9e6665c42/img-8179.jpg",
    "https://assets.auxodev.dk/assets/2f17f913-822d-430f-9ea1-0cc9e6665c42/img-8533.jpg",
    "https://assets.auxodev.dk/assets/2f17f913-822d-430f-9ea1-0cc9e6665c42/img-8076.jpg",
    "https://assets.auxodev.dk/assets/2f17f913-822d-430f-9ea1-0cc9e6665c42/sct-j-c3-b8rgens-bakke-a.jpg",
    "https://assets.auxodev.dk/assets/2f17f913-822d-430f-9ea1-0cc9e6665c42/img-8211.jpg",
    "https://assets.auxodev.dk/assets/2f17f913-822d-430f-9ea1-0cc9e6665c42/60a9493795d11_2aa3c9eb.webp"
  ];

  const [displayImages, setDisplayImages] = useState(() =>
    [...fallbackImages].sort(() => 0.5 - Math.random())
  );

  useEffect(() => {
    async function loadData() {
      const galleryData = await fetchCmsCollection("galleri");
      setGalleries(galleryData || []);

      const validGalleries = (galleryData || []).filter(
        (g) => g.id !== "ff24bfb4-6987-4b57-97bf-29526cd704bd" && g.title !== "Renovering"
      );

      const allImgs = Array.from(
        new Set(
          validGalleries.flatMap((g) => {
            const list = [];
            if (Array.isArray(g.cover_image) && g.cover_image[0]) list.push(g.cover_image[0]);
            if (Array.isArray(g.images)) list.push(...g.images);
            return list;
          }).filter(Boolean)
        )
      );

      if (allImgs.length > 0) {
        // Shuffle images randomly so 3 random images from the gallery are shown
        setDisplayImages([...allImgs].sort(() => 0.5 - Math.random()));
      }
    }
    loadData();
  }, []);

  // Autoplay for the image slider
  useEffect(() => {
    if (isPaused || displayImages.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % displayImages.length);
    }, 3500);
    return () => clearInterval(timer);
  }, [isPaused, displayImages.length]);

  const openLightbox = (imagesArr, index = 0) => {
    setSelectedGalleryImages(imagesArr);
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % displayImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + displayImages.length) % displayImages.length);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f3f4f6]">
      <SEO
        title="Kokholm Byg"
        description="Søger du et professionelt byggefirma til alt fra fliselægning til renovering af badeværelse? Vi tilbyder kvalitet i alt vi laver. Kontakt os for et tilbud."
      />

      {/* Hero Section */}
      <section 
        style={{ marginTop: 'calc(var(--spacing) * 18)' }}
        className="relative bg-[#1e2329] text-white pt-20 pb-20 lg:pt-24 lg:pb-24 overflow-hidden border-b border-[#2d343f]"
      >
        {/* Background Image with Transparent Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/2f17f913-822d-430f-9ea1-0cc9e6665c42/breaker.jpg"
            alt="Murerarbejde og byggeri i Holstebro"
            className="w-full h-full object-cover opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1e2329]/80 via-[#1e2329]/50 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl space-y-6">
            <div className="inline-flex items-center gap-2 bg-[#272d36] border border-[#373f4b] text-[#c06228] px-3.5 py-1.5 rounded text-xs font-semibold uppercase tracking-wider">
              <i className="fa-solid fa-location-dot" aria-hidden="true" />
              <span>Lokal byggepartner i Holstebro & Omegn</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Moderne og stabilt byggefirma med mere end 30 års erfaring
            </h1>

            <p className="text-base sm:text-lg text-gray-300 leading-relaxed font-normal">
              Hos Kokholm Byg bygger vi på solidt håndværk, pålidelighed og lokal forankring. Vi udfører alt fra fliselægning og badeværelsesrenovering til reparationer, tilbygninger og nybyggeri.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <Link
                to="/kontakt"
                className="btn-cta text-center px-7 py-3.5 text-base font-semibold rounded shadow-lg hover:shadow-xl transition-all text-white"
              >
                Få et uforpligtende tilbud
              </Link>
              <Link
                to="/galleri"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-[#272d36] hover:bg-[#323945] text-white border border-[#373f4b] text-base font-semibold rounded transition-colors"
              >
                <span>Se vores arbejde</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </div>

            {/* Quality Badges */}
            <div className="pt-6 grid grid-cols-2 sm:grid-cols-3 gap-4 border-t border-[#2d343f]">
              <div className="flex items-center gap-2.5 text-xs sm:text-sm text-gray-300">
                <i className="fa-solid fa-circle-check text-[#c06228]" aria-hidden="true" />
                <span>Mere end 30 års erfaring</span>
              </div>
              <div className="flex items-center gap-2.5 text-xs sm:text-sm text-gray-300">
                <i className="fa-solid fa-shield-halved text-[#c06228]" aria-hidden="true" />
                <span>Solidt håndværk</span>
              </div>
              <div className="flex items-center gap-2.5 text-xs sm:text-sm text-gray-300 col-span-2 sm:col-span-1">
                <i className="fa-solid fa-user-shield text-[#c06228]" aria-hidden="true" />
                <span>Private & Erhverv</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduktion Section */}
      <section className="py-16 lg:py-24 bg-[#f3f4f6] border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
            
            <div className="lg:col-span-7 space-y-6 flex flex-col justify-center">
              <div className="inline-block w-fit bg-white text-[#1e2329] border border-gray-200 text-xs font-bold uppercase tracking-wider px-3.5 py-1.5 rounded-full shadow-xs">
                Velkommen hos Kokholm Byg
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1e2329] tracking-tight">
                Kvalitetsbevidst murerfirma i Holstebro
              </h2>

              <div className="prose prose-slate text-gray-700 leading-relaxed text-sm sm:text-base space-y-4 font-normal">
                <p className="font-semibold text-gray-900">
                  Med mere end 30 års erfaring har vi i Kokholm Byg opbygget et firma, der holder fast i høj faglig kvalitet, solidt håndværk og nytænkning.
                </p>
                <p>
                  At stå over for et byggeprojekt kan være en stor mundfuld for mange. Hos Kokholm Byg i Holstebro har vi mange års erfaring inden for murerbranchen, og kan løse mange forskellige opgaver.
                </p>
                <p>
                  Vi rådgiver gerne i beslutningsprocessen, der sikrer, at du kommer godt i gang med byggeprojektet. Vi ved, hvad der er værd at vide om tilbygninger, nybyggeri, renovering og reparation af murværk og facader og ombygning af badeværelser.
                </p>
                <p>
                  Går du lige nu i byggetanker, og søger et kvalitetsbevidst murerfirma, så kontakt Kokholm Byg for mere information samt et godt og uforpligtende tilbud på dit næste byggeprojekt.
                </p>
              </div>

              <div className="pt-2 flex items-center gap-4">
                <Link
                  to="/om-kokholm-byg"
                  className="inline-flex items-center gap-2 font-semibold text-[#c06228] hover:text-[#aa521d] transition-colors"
                >
                  <span>Læs mere om Kokholm Byg</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 relative flex flex-col h-full">
              <div className="relative bg-white border border-gray-200 rounded-xl shadow-md h-full flex-1">
                <div className="overflow-hidden rounded-xl w-full h-full">
                  <img
                    src="https://assets.auxodev.dk/assets/2f17f913-822d-430f-9ea1-0cc9e6665c42/kokholmbyg-slide1-0.jpg"
                    alt="Kokholm Byg murerarbejde"
                    className="w-full h-full min-h-[320px] object-cover"
                  />
                </div>
                <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6 bg-[#1e2329] text-white p-4 sm:p-5 rounded-lg shadow-xl border border-[#373f4b] hidden sm:block z-20">
                  <p className="text-2xl font-bold text-[#c06228]">30+ år</p>
                  <p className="text-xs text-gray-300 font-medium">Erfaring med dansk byggeri</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Ydelser Section */}
      <section className="py-16 lg:py-24 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-3">
            <span className="text-[#c06228] text-xs font-bold uppercase tracking-wider bg-[#c06228]/10 px-3.5 py-1.5 rounded-full">
              Vores ekspertise
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1e2329] tracking-tight">
              Hvad kan vi hjælpe dig med?
            </h2>
            <p className="text-gray-600 text-sm sm:text-base">
              Vi tilbyder fagligt murer- og byggearbejde i Holstebro, Herning, Viborg, Ringkøbing og i en 50 km radius.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {SERVICES_LIST.map((service) => (
              <Link
                key={service.slug}
                to={`/vi-tilbyder/${service.slug.replace('vi-tilbyder-', '')}`}
                className="group bg-[#f3f4f6] border border-gray-200 rounded-xl p-7 hover:border-[#c06228] hover:shadow-lg transition-all duration-200 flex flex-col justify-between"
              >
                <div>
                  <div className="w-14 h-14 bg-[#1e2329] text-[#c06228] rounded-lg flex items-center justify-center text-2xl mb-6 group-hover:scale-105 group-hover:bg-[#c06228] group-hover:text-white transition-all duration-200">
                    <i className={service.icon} aria-hidden="true" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1e2329] mb-3 group-hover:text-[#c06228] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6">
                    {service.shortDescription}
                  </p>
                </div>

                <div className="flex items-center gap-2 text-sm font-semibold text-[#c06228] group-hover:translate-x-1 transition-transform">
                  <span>Læs mere om {service.title.toLowerCase()}</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </div>
              </Link>
            ))}
          </div>

        </div>
      </section>

      {/* Udvalgte Projekter / Autoplay Slider (No text on images) */}
      <section className="py-16 lg:py-24 bg-[#f3f4f6] border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <span className="text-[#c06228] text-xs font-bold uppercase tracking-wider bg-[#c06228]/10 px-3.5 py-1.5 rounded-full">
                Dokumenteret kvalitet
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1e2329] tracking-tight mt-2">
                Udvalgte projekter
              </h2>
            </div>
            <Link
              to="/galleri"
              className="inline-flex items-center gap-2 font-semibold text-[#c06228] hover:text-[#aa521d] transition-colors"
            >
              <span>Se alle billedegallerier</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          {/* Autoplay Carousel Slider Container */}
          <div
            className="relative"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div className="p-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
                {[0, 1, 2].map((offset) => {
                  const idx = (currentSlide + offset) % displayImages.length;
                  const imgUrl = displayImages[idx];
                  return (
                    <div
                      key={`${imgUrl}-${idx}-${offset}`}
                      onClick={() => openLightbox(displayImages, idx)}
                      className={`group relative cursor-pointer aspect-4/3 overflow-hidden rounded-xl border border-gray-200 shadow-md bg-gray-100 transition-all duration-700 ease-in-out hover:shadow-xl ${
                        offset === 1 ? 'hidden md:block' : ''
                      } ${offset === 2 ? 'hidden lg:block' : ''}`}
                    >
                      <img
                        src={imgUrl}
                        alt="Projektbillede"
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Slider Navigation Buttons */}
            <button
              type="button"
              onClick={prevSlide}
              aria-label="Forrige billede"
              className="absolute left-2 sm:-left-5 top-1/2 -translate-y-1/2 w-11 h-11 bg-white hover:bg-[#c06228] text-[#1e2329] hover:text-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center transition-all z-20"
            >
              <i className="fa-solid fa-chevron-left text-sm" aria-hidden="true" />
            </button>

            <button
              type="button"
              onClick={nextSlide}
              aria-label="Næste billede"
              className="absolute right-2 sm:-right-5 top-1/2 -translate-y-1/2 w-11 h-11 bg-white hover:bg-[#c06228] text-[#1e2329] hover:text-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center transition-all z-20"
            >
              <i className="fa-solid fa-chevron-right text-sm" aria-hidden="true" />
            </button>
          </div>

        </div>
      </section>

      {/* Hvorfor vælge Kokholm Byg - Restyled Split Composition with Overlapping Card */}
      <section className="py-16 lg:py-24 bg-[#f3f4f6] text-[#1e2329] border-b border-gray-200 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Left Column: Primary Text Hierarchy */}
            <div className="lg:col-span-6 space-y-6">
              <div className="inline-flex items-center gap-2 bg-[#c06228]/10 text-[#c06228] border border-[#c06228]/20 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider">
                <i className="fa-solid fa-award" aria-hidden="true" />
                <span>Dine fordele</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1e2329] tracking-tight leading-tight">
                Hvorfor vælge Kokholm Byg?
              </h2>

              <p className="text-gray-600 text-base sm:text-lg leading-relaxed font-normal">
                Som din lokale byggepartner i Holstebro bygger vi på klare aftaler, stærkt håndværk og mere end 30 års erfaring med holdbare murerløsninger.
              </p>

              {/* Large Statistic Figure */}
              <div className="py-4 flex items-center gap-5 border-y border-gray-200">
                <span className="text-5xl sm:text-6xl font-black text-[#c06228] tracking-tight">30+</span>
                <div>
                  <p className="text-lg font-bold text-[#1e2329]">Års faglig erfaring</p>
                  <p className="text-sm text-gray-500">Solidt dansk håndværk & rådgivning</p>
                </div>
              </div>

              <div>
                <Link
                  to="/kontakt"
                  className="btn-cta inline-flex items-center justify-center px-7 py-3.5 text-base font-semibold rounded shadow-md hover:shadow-lg transition-all text-white"
                >
                  Få et uforpligtende tilbud
                </Link>
              </div>
            </div>

            {/* Right Column: Prominent Image */}
            <div className="lg:col-span-6 relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-4/3 group border border-gray-200">
                <img
                  src="https://assets.auxodev.dk/assets/2f17f913-822d-430f-9ea1-0cc9e6665c42/kokholmbyg-slide1-0.jpg"
                  alt="Kokholm Byg murerarbejde og kvalitet"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </div>

          </div>

          {/* Overlapping Feature Card floating across boundary */}
          <div className="relative z-20 max-w-5xl mx-auto mt-12 lg:mt-20 bg-white border border-gray-200 shadow-2xl rounded-2xl p-6 sm:p-8 lg:p-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 divide-y md:divide-y-0 md:divide-x divide-gray-200">
              
              <div className="flex items-start gap-4 pr-0 md:pr-4">
                <div className="w-12 h-12 rounded-xl bg-[#c06228]/10 text-[#c06228] flex items-center justify-center text-xl shrink-0">
                  <i className="fa-solid fa-shield-halved" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#1e2329]">Solidt & Holdbart Håndværk</h3>
                  <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                    Vi bygger til at holde i mange år fremover og følger nøje alle byggefaglige standarder og vådrumskrav.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 pt-6 md:pt-0 pl-0 md:pl-8">
                <div className="w-12 h-12 rounded-xl bg-[#c06228]/10 text-[#c06228] flex items-center justify-center text-xl shrink-0">
                  <i className="fa-solid fa-comments" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#1e2329]">Tydelig Kommunikation</h3>
                  <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                    Du er i trygge hænder med direkte kontakt til mureren og klare aftaler om pris og tidshorisont.
                  </p>
                </div>
              </div>

            </div>
          </div>

        </div>
      </section>

      {/* Form & Contact Section - Restyled based on Design Reference #332ab29e-37ca-439b-a7f3-c965a6ba9573 */}
      <section className="relative">
        {/* Main Hero Block with full width background overlay */}
        <div className="relative bg-[#1e2329] text-white py-16 lg:py-24 border-b border-[#2d343f] overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://assets.auxodev.dk/assets/2f17f913-822d-430f-9ea1-0cc9e6665c42/kokholmbyg-slide1-0_1.webp"
              alt="Byggeplads baggrund"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-[#1e2329]/60" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left Content Area (~2/3 width) */}
            <div className="lg:col-span-7 space-y-6">
              <span className="inline-block bg-[#c06228] text-white px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider shadow-sm">
                Kontakt os
              </span>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight">
                Lad os drøfte dit næste byggeprojekt
              </h2>

              <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
                Du kan komme i kontakt med os ved at ringe, skrive en e-mail eller udfylde formularen. Vi vender hurtigt tilbage med kompetent rådgivning og et uforpligtende tilbud.
              </p>

              <ul className="space-y-3 pt-2 text-gray-200 text-sm sm:text-base font-medium">
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-circle-check text-[#c06228] text-lg" aria-hidden="true" />
                  <span>Uforpligtende rådgivning og hurtigt tilbud</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-circle-check text-[#c06228] text-lg" aria-hidden="true" />
                  <span>Dækker Holstebro, Herning, Viborg, Ringkøbing og omegn</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-circle-check text-[#c06228] text-lg" aria-hidden="true" />
                  <span>Mere end 30 års erfaring som murer & byggefirma</span>
                </li>
              </ul>

              {/* Primary & Secondary Buttons */}
              <div className="flex flex-wrap gap-4 pt-4">
                <a
                  href={`tel:${COMPANY_INFO.phoneRaw}`}
                  className="btn-cta px-6 py-3.5 rounded-full text-white font-semibold text-sm shadow-lg flex items-center gap-2 hover:scale-105 transition-transform"
                >
                  <i className="fa-solid fa-phone" aria-hidden="true" />
                  <span className="whitespace-nowrap">Ring: {COMPANY_INFO.phone}</span>
                </a>

                <a
                  href={`mailto:${COMPANY_INFO.email}`}
                  className="px-6 py-3.5 rounded-full text-white font-semibold text-sm border border-white/30 hover:bg-white/10 transition-colors flex items-center gap-2"
                >
                  <i className="fa-solid fa-envelope text-[#c06228]" aria-hidden="true" />
                  <span className="whitespace-nowrap">{COMPANY_INFO.email}</span>
                </a>
              </div>
            </div>

            {/* Right Side: Prominent Form Card */}
            <div className="lg:col-span-5">
              <div className="bg-white text-[#1e2329] p-6 sm:p-8 rounded-xl shadow-2xl border border-gray-100">
                <WebForm />
              </div>
            </div>

          </div>
        </div>

        {/* Bottom Banner: 4 evenly spaced icon-and-text pairs */}
        <div className="bg-[#181d22] border-t border-[#2d343f] py-8 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            
            <div className="flex items-center gap-4 p-2">
              <div className="w-12 h-12 rounded-full bg-[#c06228]/20 text-[#c06228] flex items-center justify-center text-xl shrink-0">
                <i className="fa-solid fa-phone" aria-hidden="true" />
              </div>
              <div>
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Telefon</p>
                <a href={`tel:${COMPANY_INFO.phoneRaw}`} className="text-sm font-bold text-white hover:text-[#c06228] transition-colors whitespace-nowrap">
                  {COMPANY_INFO.phone}
                </a>
              </div>
            </div>

            <div className="flex items-center gap-4 p-2">
              <div className="w-12 h-12 rounded-full bg-[#c06228]/20 text-[#c06228] flex items-center justify-center text-xl shrink-0">
                <i className="fa-solid fa-envelope" aria-hidden="true" />
              </div>
              <div>
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">E-mail</p>
                <a href={`mailto:${COMPANY_INFO.email}`} className="text-sm font-bold text-white hover:text-[#c06228] transition-colors whitespace-nowrap">
                  {COMPANY_INFO.email}
                </a>
              </div>
            </div>

            <div className="flex items-center gap-4 p-2">
              <div className="w-12 h-12 rounded-full bg-[#c06228]/20 text-[#c06228] flex items-center justify-center text-xl shrink-0">
                <i className="fa-solid fa-location-dot" aria-hidden="true" />
              </div>
              <div>
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Adresse</p>
                <p className="text-sm font-bold text-white">Tingagerparken 11, Holstebro</p>
              </div>
            </div>

            <div className="flex items-center gap-4 p-2">
              <div className="w-12 h-12 rounded-full bg-[#c06228]/20 text-[#c06228] flex items-center justify-center text-xl shrink-0">
                <i className="fa-solid fa-clock" aria-hidden="true" />
              </div>
              <div>
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Arbejdstid</p>
                <p className="text-sm font-bold text-white">Man - Fre: 07:00 - 17:00</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <LightboxModal
        isOpen={lightboxOpen}
        images={selectedGalleryImages}
        currentIndex={currentImageIndex}
        onClose={() => setLightboxOpen(false)}
        onPrev={() => setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : selectedGalleryImages.length - 1))}
        onNext={() => setCurrentImageIndex((prev) => (prev < selectedGalleryImages.length - 1 ? prev + 1 : 0))}
      />
    </div>
  );
}
